import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { scannerAPI } from '../lib/api';
import { cardTheme, resolveAssetUrl as resolveUrl, StampGrid, SafeImage } from '../components/loyalty/LoyaltyCard';
import { Check, Gift, Loader2, LogIn, Undo2, Plus, QrCode, LayoutDashboard, Share2 } from 'lucide-react';

/*
 * /s/:memberCode — the screen a merchant gets when scanning a customer's
 * Wallet card with the iPhone camera (Safari opens this URL).
 *
 * Flow: card of the merchant in its own colours → one big thumb button →
 * the stamp lands on the card → "Terminé" (or "Annuler" if mis-tapped).
 * When the stamp completes the card, the card turns into "Récompense à remettre".
 */

const playSuccessSound = (reward = false) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = reward ? [880, 1174.66, 1567.98] : [880, 1174.66];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      const t = ctx.currentTime + i * 0.14;
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.setValueAtTime(0.28, t);
      gain.gain.exponentialRampToValueAtTime(0.01, t + 0.32);
      osc.start(t);
      osc.stop(t + 0.34);
    });
    setTimeout(() => ctx.close(), 900);
  } catch (e) { /* sound is a bonus */ }
};

/* ── Neutral full-screen message (login, errors, done) ── */
const Message = ({ icon: Icon, tone = 'neutral', title, children, actions }) => (
  <div className="min-h-[100dvh] bg-[#EEF0F2] flex flex-col">
    <div className="flex-1 flex flex-col justify-center px-6 py-10 max-w-md w-full mx-auto">
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${
        tone === 'success' ? 'bg-[#1F8A4C] text-white' : tone === 'warn' ? 'bg-[#F6E3B4] text-[#7A5200]' : 'bg-white text-[#15171A]'
      }`}>
        <Icon className="w-7 h-7" />
      </div>
      <h1 className="text-[28px] leading-tight font-bold text-[#15171A] mb-3">{title}</h1>
      <div className="text-[17px] leading-relaxed text-[#4A515C] space-y-2">{children}</div>
    </div>
    {actions && (
      <div className="px-6 pt-2 max-w-md w-full mx-auto" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
        {actions}
      </div>
    )}
  </div>
);

const PrimaryButton = ({ children, className = '', style, ...props }) => (
  <button
    {...props}
    style={style}
    className={`w-full h-16 rounded-2xl text-[19px] font-semibold flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:opacity-60 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#15171A] ${className}`}
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, className = '', ...props }) => (
  <button
    {...props}
    className={`w-full h-14 rounded-2xl text-[17px] font-semibold flex items-center justify-center gap-2 bg-white text-[#15171A] border border-[#D5D9DF] active:scale-[0.98] transition-transform disabled:opacity-60 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#15171A] ${className}`}
  >
    {children}
  </button>
);

const InstantScanPage = () => {
  const { memberCode } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mid = searchParams.get('mid') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null);        // { status, message }
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);      // last scan response
  const [notice, setNotice] = useState('');        // inline error / info
  const [amount, setAmount] = useState('');
  const [done, setDone] = useState(false);

  const fetchCustomer = useCallback(async () => {
    try {
      const { data: resp } = await scannerAPI.lookup(memberCode, mid || undefined);
      setData(resp);
      setSelectedId((prev) => prev || resp.cards[0]?.id || null);
    } catch (err) {
      // 401 → the api interceptor refreshes the session or sends to /login
      // (and comes back here after login).
      if (err.response?.status !== 401) {
        setFatal({ status: err.response?.status || 0, message: err.response?.data?.detail });
      }
    } finally {
      setLoading(false);
    }
  }, [memberCode, mid]);

  useEffect(() => {
    if (!authLoading && user) fetchCustomer();
    else if (!authLoading && !user) setLoading(false);
  }, [authLoading, user, fetchCustomer]);

  const card = useMemo(() => data?.cards.find((c) => c.id === selectedId) || null, [data, selectedId]);
  const theme = cardTheme(card);
  const isStamps = card?.card_type === 'stamps';
  const pointsPreview = useMemo(() => {
    const n = parseFloat(String(amount).replace(',', '.'));
    if (!card || Number.isNaN(n) || n < 1) return null;
    return Math.max(1, Math.floor(n * (card.points_per_euro || 1)));
  }, [amount, card]);

  const addOne = async () => {
    if (!card || busy) return;
    setBusy(true);
    setNotice('');
    const payload = { member_code: memberCode, card_id: card.id };
    if (!isStamps) {
      const n = parseFloat(String(amount).replace(',', '.'));
      if (Number.isNaN(n) || n < 1) { setNotice("Entrez le montant de l'achat (1 € minimum)."); setBusy(false); return; }
      if (n > 500) { setNotice('500 € maximum par passage.'); setBusy(false); return; }
      payload.amount = n;
    }
    try {
      const { data: resp } = await scannerAPI.quick(payload);
      setResult(resp);
      // Keep the card in sync: after a reward the server already reset it.
      setData((d) => ({
        ...d,
        cards: d.cards.map((c) => (c.id === card.id
          ? { ...c, balance: resp.reward_earned && isStamps ? 0 : resp.new_balance }
          : c)),
      }));
      playSuccessSound(resp.reward_earned);
      if ('vibrate' in navigator) navigator.vibrate(resp.reward_earned ? [80, 60, 80] : 60);
    } catch (err) {
      if (err.response?.status === 401) return;
      const detail = err.response?.data?.detail;
      setNotice(typeof detail === 'object' ? (detail.message || 'Le tampon n’a pas été ajouté. Réessayez.')
        : (typeof detail === 'string' ? detail : 'Le tampon n’a pas été ajouté. Vérifiez la connexion et réessayez.'));
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!result?.transaction_id || busy) return;
    setBusy(true);
    setNotice('');
    try {
      const { data: resp } = await scannerAPI.undo(result.transaction_id);
      setData((d) => ({ ...d, cards: d.cards.map((c) => (c.id === resp.card_id ? { ...c, balance: resp.balance } : c)) }));
      setResult(null);
      setNotice(isStamps ? 'Tampon annulé. La carte est revenue comme avant.' : 'Points annulés. La carte est revenue comme avant.');
    } catch (err) {
      if (err.response?.status === 401) return;
      const detail = err.response?.data?.detail;
      setNotice(typeof detail === 'string' ? detail : 'Annulation impossible. Corrigez depuis la fiche du client.');
    } finally {
      setBusy(false);
    }
  };

  const shareWalletLink = async () => {
    const url = `${window.location.origin}/wallet/${memberCode}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Votre carte de fidélité', url });
      else { await navigator.clipboard.writeText(url); setNotice('Lien de la carte copié.'); }
    } catch (e) { /* share sheet closed */ }
  };

  /* ── Loading ── */
  if (authLoading || loading) {
    return (
      <div className="min-h-[100dvh] bg-[#EEF0F2] px-5 pt-6">
        <div className="max-w-md mx-auto">
          <div className="h-[260px] rounded-[22px] bg-[#DCE0E5] animate-pulse" />
          <div className="sr-only">Chargement de la carte du client…</div>
        </div>
      </div>
    );
  }

  /* ── Not logged in ── */
  if (!user) {
    const redirectPath = mid ? `/s/${memberCode}?mid=${mid}` : `/s/${memberCode}`;
    return (
      <Message
        icon={LogIn}
        title="Connectez-vous pour tamponner cette carte"
        actions={(
          <PrimaryButton
            className="bg-[#15171A] text-white"
            onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`)}
            data-testid="instant-scan-login-btn"
          >
            <LogIn className="w-5 h-5" /> Se connecter
          </PrimaryButton>
        )}
      >
        <p>Une seule fois sur ce téléphone. Après la connexion, vous revenez directement sur la carte du client (<span className="whitespace-nowrap">{memberCode}</span>).</p>
      </Message>
    );
  }

  /* ── Errors that stop the flow ── */
  if (fatal) {
    const foreign = fatal.status === 403;
    return (
      <Message
        icon={QrCode}
        tone="warn"
        title={foreign ? 'Ce client n’a pas votre carte' : 'Carte introuvable'}
        actions={(
          <div className="space-y-3">
            {foreign && (
              <PrimaryButton className="bg-[#15171A] text-white" onClick={() => navigate('/dashboard/store-qr')}>
                <QrCode className="w-5 h-5" /> Afficher le QR de ma boutique
              </PrimaryButton>
            )}
            <SecondaryButton onClick={() => navigate('/dashboard')} data-testid="instant-scan-back-btn">
              <LayoutDashboard className="w-5 h-5" /> Tableau de bord
            </SecondaryButton>
          </div>
        )}
      >
        {foreign ? (
          <p>Ce QR vient de la carte d’un autre commerce. Pour s’inscrire à la vôtre, le client scanne le QR de votre boutique.</p>
        ) : (
          <p>Ce QR ne correspond à aucun client (<span className="whitespace-nowrap">{memberCode}</span>). Vérifiez qu’il s’agit bien d’une carte LoyalPass de votre commerce.</p>
        )}
      </Message>
    );
  }

  if (!data) return null;
  const firstName = (data.customer.name || '').split(' ')[0];

  /* ── Done: next customer is scanned with the camera, not here ── */
  if (done) {
    return (
      <Message
        icon={Check}
        tone="success"
        title="C’est noté"
        actions={(
          <SecondaryButton onClick={() => navigate('/dashboard')} data-testid="instant-scan-dashboard-btn">
            <LayoutDashboard className="w-5 h-5" /> Tableau de bord
          </SecondaryButton>
        )}
      >
        <p>La carte de {data.customer.name} est à jour dans son Wallet.</p>
        <p>Client suivant : ouvrez l’appareil photo et scannez sa carte.</p>
      </Message>
    );
  }

  /* ── Customer has none of the merchant's active cards ── */
  if (data.cards.length === 0) {
    return (
      <Message
        icon={QrCode}
        tone="warn"
        title={`${firstName} n’est inscrit(e) à aucune de vos cartes`}
        actions={(
          <PrimaryButton className="bg-[#15171A] text-white" onClick={() => navigate('/dashboard/store-qr')} data-testid="no-cards-enrolled">
            <QrCode className="w-5 h-5" /> Afficher le QR de ma boutique
          </PrimaryButton>
        )}
      >
        <p>Pour s’inscrire, le client scanne le QR de votre boutique avec son téléphone.</p>
      </Message>
    );
  }

  /* ── Main: the merchant's card, in its colours ── */
  const total = card.card_type === 'stamps' ? card.total_stamps : card.reward_threshold;
  const reward = !!result?.reward_earned;
  const filled = isStamps ? (reward ? total : card.balance) : Math.min(card.balance, total);
  const remaining = Math.max(0, total - card.balance);
  const nextUnlocks = isStamps && !result && remaining === 1;
  const logo = resolveUrl(card.logo_url);

  return (
    <div className="min-h-[100dvh] bg-[#EEF0F2] flex flex-col" data-testid="instant-scan-card">
      <div className="flex-1 px-4 pt-4 pb-6 max-w-md w-full mx-auto">
        {data.cards.length > 1 && !result && (
          <div className="flex gap-2 mb-3 overflow-x-auto -mx-1 px-1" role="tablist" aria-label="Choisir la carte">
            {data.cards.map((c) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={c.id === selectedId}
                onClick={() => { setSelectedId(c.id); setNotice(''); setAmount(''); }}
                className={`shrink-0 h-10 px-4 rounded-full text-[15px] font-semibold border ${
                  c.id === selectedId ? 'bg-[#15171A] text-white border-[#15171A]' : 'bg-white text-[#15171A] border-[#D5D9DF]'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* The card */}
        <section
          className="rounded-[22px] p-5 shadow-[0_12px_32px_-12px_rgba(21,23,26,0.45)] scan-card"
          style={{ background: theme.bg, color: theme.fg }}
          data-testid={reward ? 'instant-scan-reward' : (result ? 'instant-scan-result' : undefined)}
        >
          <header className="flex items-center gap-3 mb-6 min-h-[40px]">
            <SafeImage key={logo || 'no-logo'} src={logo} alt="" className="h-10 w-10 rounded-xl object-contain" style={{ background: 'rgba(255,255,255,0.12)' }} />
            <p className="text-[16px] font-semibold leading-tight opacity-90 truncate">{card.name}</p>
          </header>

          {reward ? (
            <div className="scan-reward-in">
              <Gift className="w-11 h-11 mb-3" strokeWidth={1.75} />
              <h1 className="text-[30px] leading-[1.1] font-bold mb-2">Récompense à remettre</h1>
              <p className="text-[21px] leading-snug font-semibold mb-1">{result.reward_description || card.reward_description}</p>
              <p className="text-[16px] opacity-80 mb-5">pour {data.customer.name}. Sa carte repart à zéro.</p>
              {isStamps && <StampGrid total={total} filled={total} fg={theme.fg} icon={card.stamp_icon} justAdded />}
            </div>
          ) : (
            <>
              <h1 className="text-[30px] leading-[1.1] font-bold mb-1 break-words">{data.customer.name}</h1>
              {isStamps ? (
                <p className="text-[17px] opacity-85 mb-5 tabular-nums">
                  {card.balance} tampon{card.balance > 1 ? 's' : ''} sur {total}
                </p>
              ) : (
                <p className="text-[17px] opacity-85 mb-5 tabular-nums">
                  {card.balance} point{card.balance > 1 ? 's' : ''} sur {total}
                </p>
              )}

              {isStamps ? (
                <StampGrid total={total} filled={filled} fg={theme.fg} icon={card.stamp_icon} justAdded={!!result} />
              ) : (
                <div className="h-3 rounded-full overflow-hidden" style={{ background: `${theme.fg}33` }}>
                  <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, (card.balance / total) * 100)}%`, background: theme.fg }} />
                </div>
              )}

              {card.reward_description && (
                <p className="text-[15px] leading-snug mt-5 opacity-85">
                  {remaining === 0 ? 'Récompense : ' : `Encore ${remaining} ${isStamps ? `tampon${remaining > 1 ? 's' : ''}` : `point${remaining > 1 ? 's' : ''}`} avant : `}
                  <span className="font-semibold">{card.reward_description}</span>
                </p>
              )}
            </>
          )}
        </section>

        {/* Status under the card */}
        {result && !reward && (
          <p className="flex items-center gap-2 mt-4 text-[17px] font-semibold text-[#1F8A4C]" role="status">
            <Check className="w-5 h-5" strokeWidth={3} />
            {isStamps ? 'Tampon ajouté' : `${result.points_added} points ajoutés`}
          </p>
        )}
        {nextUnlocks && (
          <p className="flex items-start gap-2 mt-4 text-[16px] leading-snug text-[#15171A]" role="note">
            <Gift className="w-5 h-5 mt-0.5 shrink-0" />
            Ce tampon complète la carte : la récompense sera à remettre tout de suite.
          </p>
        )}
        {notice && (
          <p className="mt-4 text-[16px] leading-snug text-[#15171A] bg-white rounded-xl px-4 py-3 border border-[#D5D9DF]" role="alert">
            {notice}
          </p>
        )}

        {!isStamps && !result && (
          <label className="block mt-5">
            <span className="block text-[16px] font-semibold text-[#15171A] mb-2">Montant de l’achat</span>
            <div className="flex items-center h-16 rounded-2xl bg-white border border-[#D5D9DF] px-4 focus-within:border-[#15171A]">
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, '').slice(0, 7))}
                className="flex-1 bg-transparent text-[26px] font-semibold tabular-nums text-[#15171A] outline-none min-w-0"
                data-testid={`amount-input-${card.id}`}
              />
              <span className="text-[22px] font-semibold text-[#4A515C]">€</span>
            </div>
          </label>
        )}
      </div>

      {/* Thumb zone */}
      <div className="sticky bottom-0 bg-[#EEF0F2]/95 backdrop-blur px-4 pt-3 max-w-md w-full mx-auto"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
        {!result ? (
          <PrimaryButton
            onClick={addOne}
            disabled={busy}
            style={{ background: theme.bg, color: theme.fg }}
            data-testid={`stamp-card-${card.id}`}
          >
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-6 h-6" strokeWidth={2.5} />}
            {isStamps ? 'Ajouter 1 tampon' : (pointsPreview ? `Ajouter ${pointsPreview} point${pointsPreview > 1 ? 's' : ''}` : 'Ajouter les points')}
          </PrimaryButton>
        ) : (
          <div className="space-y-3">
            <PrimaryButton className="bg-[#15171A] text-white" onClick={() => setDone(true)} data-testid="instant-scan-done-btn">
              <Check className="w-6 h-6" strokeWidth={2.5} /> {reward ? 'Récompense remise' : 'Terminé'}
            </PrimaryButton>
            <div className="grid grid-cols-2 gap-3">
              <SecondaryButton onClick={undo} disabled={busy} data-testid="instant-scan-undo-btn">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Undo2 className="w-5 h-5" />} Annuler
              </SecondaryButton>
              {isStamps && !reward ? (
                <SecondaryButton onClick={addOne} disabled={busy} data-testid="instant-scan-again-btn">
                  <Plus className="w-5 h-5" /> 1 de plus
                </SecondaryButton>
              ) : (
                <SecondaryButton onClick={shareWalletLink} data-testid="share-pass-link">
                  <Share2 className="w-5 h-5" /> Lien carte
                </SecondaryButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstantScanPage;
